import axios from "axios";
import { useEffect, useState, useCallback } from "react";
import placeholderimage from "../../assets/home-images/placeholderimage.png";
// export const BACKEND_URL = "http://localhost:8000/api/";
// export const ImageURL = "http://localhost:8000/uploads/";

export const BACKEND_URL = "https://hayatinapp.com/api/";
export const ImageURL = "https://hayatinapp.com/uploads/";
export const PLACEHOLDER_IMAGE = placeholderimage;

// Optimized axios instance with fast defaults for all API calls
export const fastApiClient = axios.create({
    timeout: 10000, // 10s timeout - allows API to complete but not too long
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    },
    withCredentials: false
});

/**
 * Optimized API call wrapper that ensures loading state is hidden immediately after response
 * This makes data display faster by hiding loading before data processing
 * 
 * Usage:
 * await fastApiCall(
 *   () => axios.get(`${BACKEND_URL}website/categories`),
 *   setIsLoading,
 *   setData
 * );
 * 
 * @param {Function} apiCall - The axios API call function (e.g., () => axios.get(url))
 * @param {Function} setLoading - Function to set loading state (e.g., setIsLoading)
 * @param {Function} setData - Function to set data state (e.g., setData)
 * @param {Function} dataExtractor - Optional function to extract/transform data from response
 * @returns {Promise} - The API response
 */
export const fastApiCall = async (apiCall, setLoading, setData, dataExtractor = null) => {
    if (setLoading) setLoading(true);
    
    try {
        const response = await apiCall();
        
        // Hide loading IMMEDIATELY - before any data processing
        if (setLoading) setLoading(false);
        
        // Extract data (if custom extractor provided, use it; otherwise use response.data)
        const data = dataExtractor ? dataExtractor(response.data) : response.data;
        
        // Set data immediately for fastest rendering
        if (setData) setData(data);
        
        return response;
    } catch (error) {
        // Hide loading on error too
        if (setLoading) setLoading(false);
        
        // Set empty data on error (default to empty array for most cases)
        if (setData) {
            setData([]);
        }
        
        throw error;
    }
};

/**
 * React hook for optimized API calls with automatic loading state management
 * 
 * Usage:
 * const { data, loading, error, refetch } = useFastApiCall(
 *   () => axios.get(`${BACKEND_URL}website/categories`),
 *   { initialData: [] }
 * );
 * 
 * @param {Function} apiCall - The axios API call function
 * @param {Object} options - Options object
 * @param {*} options.initialData - Initial data value (default: [])
 * @param {Function} options.dataExtractor - Optional data extraction function
 * @param {Array} options.deps - Dependency array for useEffect (default: [])
 * @returns {Object} - { data, loading, error, refetch }
 */
export const useFastApiCall = (apiCall, options = {}) => {
    const { initialData = [], dataExtractor = null, deps = [] } = options;
    const [data, setData] = useState(initialData);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const execute = useCallback(async () => {
        setError(null);
        try {
            await fastApiCall(apiCall, setLoading, setData, dataExtractor);
        } catch (err) {
            setError(err);
        }
    }, [apiCall, dataExtractor]);

    useEffect(() => {
        execute();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps.length > 0 ? deps : [execute]);

    return { data, loading, error, refetch: execute };
};

// User Lat Lang
const useEmirateFromCoordinates = (latitude, longitude) => {
  const [emirate, setEmirate] = useState(null);

  useEffect(() => {
    if (!latitude || !longitude) return;

    const fetchEmirate = async () => {
      try {
        // Replace YOUR_GOOGLE_MAPS_API_KEY with your actual key
        const response = await axios.get(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyCPHk4hem15xLFDAPWiSP6G29Xf9J44qX4`
        );

        const results = response.data.results;
        const adminArea = results
          ?.flatMap((r) => r.address_components)
          .find((comp) => comp.types.includes("administrative_area_level_1"))
          ?.long_name.toLowerCase();

        if (!adminArea) {
          setEmirate(null);
          return;
        }

        let emiratePair;
        if (adminArea.includes("abu dhabi") || adminArea.includes("أبو ظبي")) {
          emiratePair = { name: "Abu Dhabi", id: 1 };
        } else if (adminArea.includes("dubai") || adminArea.includes("دبي")) {
          emiratePair = { name: "Dubai", id: 2 };
        } else {
          emiratePair = { name: "Dubai", id: 2 }; 
        }

        setEmirate(emiratePair);
      } catch (error) {
        console.error("Error fetching geocode data:", error);
        setEmirate(null);
      }
    };

    fetchEmirate();
  }, [latitude, longitude]);

  return emirate;
};
export default useEmirateFromCoordinates;

